import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-multi-date-picker";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persianFa from "react-date-object/locales/persian_fa";
import { Filter, RefreshCw, Search, Trash2, X } from "lucide-react";

import { deleteExportCottages, fetchExportCottages } from "../actions/cottageActions";
import FilterSlideOver, { AccordionItem } from "./ui/FilterSlideOver";
import ConfirmModal from "./ui/ConfirmModal";
import HotToastMessage from "./ui/HotToastMessage";
import PaginationControls from "./PaginationControls";

const EMPTY_FILTERS = {
  dateFrom: "",
  dateTo: "",
  paymentFrom: "",
  paymentTo: "",
  status: "",
  currency: "",
  customs: "",
  destination: "",
  transport: "",
  valueMin: "",
  valueMax: "",
};

const normalizeText = (value) => String(value ?? "").trim().toLocaleLowerCase();
const westernDigits = (value) => String(value ?? "").replace(/[۰-۹]/g, (digit) => "۰۱۲۳۴۵۶۷۸۹".indexOf(digit));
const dateKey = (value) => westernDigits(value).replace(/\D/g, "");
const formatNumber = (value) =>
  value === null || value === undefined || value === ""
    ? "—"
    : Number(value).toLocaleString("fa-IR", { maximumFractionDigits: 2 });

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:ring-4 focus:ring-blue-100";

function DateFilter({ label, value, onChange }) {
  const pickerValue = value
    ? new DateObject({ date: value, format: "YYYY-MM-DD", calendar: persian, locale: persianFa })
    : null;
  return (
    <label className="block space-y-1.5 text-sm text-slate-700">
      <span className="font-medium">{label}</span>
      <DatePicker
        value={pickerValue}
        onChange={(date) => onChange(date?.format ? westernDigits(date.format("YYYY-MM-DD")) : "")}
        calendar={persian}
        locale={persianFa}
        format="YYYY/MM/DD"
        inputClass={inputClass}
        calendarPosition="bottom-right"
        placeholder="انتخاب تاریخ"
      />
    </label>
  );
}

function FilterSelect({ label, value, options, onChange }) {
  return (
    <label className="block space-y-1.5 text-sm text-slate-700">
      <span className="font-medium">{label}</span>
      <select className={inputClass} value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">همه</option>
        {options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    </label>
  );
}

const ExportCottageList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const { cottages = [], loading, error } = useSelector((state) => state.exportCottages || {});

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState({ field: "cottage_date", descending: true });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "info" });

  useEffect(() => {
    if (auth.isAuthenticated === false) navigate("/");
  }, [auth.isAuthenticated, navigate]);

  useEffect(() => {
    dispatch(fetchExportCottages());
  }, [dispatch]);

  const distinctOptions = useMemo(() => {
    const fields = ["declaration_status", "currency_type", "customs_name", "destination_country", "transport_method"];
    return Object.fromEntries(fields.map((field) => [
      field,
      [...new Set(cottages.map((item) => String(item[field] ?? "").trim()).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, "fa")),
    ]));
  }, [cottages]);

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(Boolean).length + Number(Boolean(search.trim())),
    [filters, search]
  );

  const filteredCottages = useMemo(() => {
    const query = normalizeText(westernDigits(search));
    const startDate = dateKey(filters.dateFrom);
    const endDate = dateKey(filters.dateTo);
    const paymentStart = dateKey(filters.paymentFrom);
    const paymentEnd = dateKey(filters.paymentTo);
    const minValue = filters.valueMin === "" ? null : Number(westernDigits(filters.valueMin));
    const maxValue = filters.valueMax === "" ? null : Number(westernDigits(filters.valueMax));

    return cottages.filter((cottage) => {
      const searchable = [
        cottage.full_serial_number,
        cottage.declaration_status,
        cottage.currency_type,
        cottage.customs_name,
        cottage.exit_customs_name,
        cottage.destination_country,
        cottage.transport_method,
        ...(cottage.cottage_goods || []).flatMap((good) => [good.HSCode, good.goods_description]),
      ].map((value) => normalizeText(westernDigits(value))).join(" ");

      const declarationDate = dateKey(cottage.cottage_date);
      const paymentDate = dateKey(cottage.cottage_payment_date);
      const value = Number(cottage.total_value);

      return (!query || searchable.includes(query)) &&
        (!startDate || declarationDate >= startDate) &&
        (!endDate || declarationDate <= endDate) &&
        (!paymentStart || (paymentDate && paymentDate >= paymentStart)) &&
        (!paymentEnd || (paymentDate && paymentDate <= paymentEnd)) &&
        (!filters.status || cottage.declaration_status === filters.status) &&
        (!filters.currency || cottage.currency_type === filters.currency) &&
        (!filters.customs || cottage.customs_name === filters.customs) &&
        (!filters.destination || cottage.destination_country === filters.destination) &&
        (!filters.transport || cottage.transport_method === filters.transport) &&
        (minValue === null || value >= minValue) &&
        (maxValue === null || value <= maxValue);
    });
  }, [cottages, filters, search]);

  const sortedCottages = useMemo(() => {
    const direction = sort.descending ? -1 : 1;
    return [...filteredCottages].sort((left, right) => {
      const a = left[sort.field];
      const b = right[sort.field];
      if (["total_value", "quantity"].includes(sort.field)) {
        return ((Number(a) || 0) - (Number(b) || 0)) * direction;
      }
      return String(a ?? "").localeCompare(String(b ?? ""), "fa", { numeric: true }) * direction;
    });
  }, [filteredCottages, sort]);

  const totalPages = Math.max(1, Math.ceil(sortedCottages.length / pageSize));
  const pageItems = sortedCottages.slice((page - 1) * pageSize, page * pageSize);
  const visibleIds = pageItems.map((item) => item.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  useEffect(() => {
    setPage(1);
  }, [search, filters, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const updateFilter = (key, value) => setFilters((previous) => ({ ...previous, [key]: value }));
  const toggleSort = (field) => setSort((previous) => ({
    field,
    descending: previous.field === field ? !previous.descending : field === "cottage_date",
  }));
  const sortMark = (field) => sort.field === field ? (sort.descending ? " ↓" : " ↑") : "";

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearch("");
    setSelectedIds([]);
  };

  const toggleSelected = (id) => setSelectedIds((previous) =>
    previous.includes(id) ? previous.filter((item) => item !== id) : [...previous, id]
  );

  const togglePageSelection = () => setSelectedIds((previous) =>
    allVisibleSelected
      ? previous.filter((id) => !visibleIds.includes(id))
      : [...new Set([...previous, ...visibleIds])]
  );

  const onConfirmDelete = async () => {
    setConfirmDelete(false);
    try {
      await dispatch(deleteExportCottages(selectedIds));
      setSelectedIds([]);
      await dispatch(fetchExportCottages());
      setToast({ message: "اظهارنامه‌های انتخاب‌شده حذف شدند.", type: "success" });
    } catch (deleteError) {
      setToast({ message: "حذف اظهارنامه‌ها انجام نشد.", type: "error" });
    }
  };

  const columnButton = (label, field, className = "") => (
    <button type="button" onClick={() => toggleSort(field)} className={`whitespace-nowrap font-semibold hover:text-blue-700 ${className}`}>
      {label}{sortMark(field)}
    </button>
  );

  return (
    <div className="mt-24 min-h-screen bg-slate-50 pb-24 font-vazir" dir="rtl">
      <HotToastMessage
        message={toast.message}
        type={toast.type}
        duration={3000}
        onClose={() => setToast({ message: "", type: "info" })}
        position="top"
      />
      <ConfirmModal
        isOpen={confirmDelete}
        onConfirm={onConfirmDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <main className="mx-auto max-w-[90%] px-4 sm:px-6 lg:px-8">
        <section className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900 sm:text-3xl">اظهارنامه‌های صادراتی</h1>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-slate-100 px-3 py-1.5 text-slate-700">نتایج: {filteredCottages.length.toLocaleString("fa-IR")} از {cottages.length.toLocaleString("fa-IR")}</span>
                <span className={`rounded-full px-3 py-1.5 ${activeFilterCount ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                  {activeFilterCount ? `${activeFilterCount.toLocaleString("fa-IR")} فیلتر فعال` : "بدون فیلتر"}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800"
              >
                <Filter className="h-4 w-4" /> فیلترها
              </button>
              <button
                type="button"
                onClick={() => dispatch(fetchExportCottages())}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2.5 text-sm font-medium text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> تازه‌سازی
              </button>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2.5 text-sm text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                >
                  <X className="h-4 w-4" /> پاک‌سازی
                </button>
              )}
            </div>
          </div>

          <label className="mt-5 block text-sm font-medium text-slate-700">
            جستجوی شماره کوتاژ، وضعیت، گمرک، کشور یا شرح کالا
            <span className="relative mt-2 block">
              <Search className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-4 pr-12 text-sm font-normal text-slate-800 outline-none focus:ring-4 focus:ring-blue-100"
                placeholder="برای جستجو تایپ کنید…"
              />
            </span>
          </label>

          {loading && (
            <div className="mt-6 grid gap-3" aria-label="در حال بارگذاری">
              <div className="h-10 animate-pulse rounded-2xl bg-slate-100" />
              <div className="h-10 animate-pulse rounded-2xl bg-slate-100" />
            </div>
          )}
          {error && !loading && (
            <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {typeof error === "string" ? error : JSON.stringify(error)}
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="mt-5">
                <PaginationControls
                  currentPage={page}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  pageSizeOptions={[10, 20, 50, 100]}
                  hasNext={page < totalPages}
                  hasPrevious={page > 1}
                  onPageChange={(event) => setPage(Number(event.target.value))}
                  onNextPage={() => setPage((previous) => Math.min(totalPages, previous + 1))}
                  onPreviousPage={() => setPage((previous) => Math.max(1, previous - 1))}
                  onPageSizeChange={(event) => setPageSize(Number(event.target.value))}
                />
              </div>

              {pageItems.length === 0 ? (
                <div className="mt-8 rounded-3xl bg-slate-50 px-5 py-12 text-center text-slate-600">
                  اظهارنامه‌ای با این جستجو و فیلترها پیدا نشد.
                </div>
              ) : (
                <div className="mt-4 overflow-hidden rounded-3xl ring-1 ring-slate-200">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 bg-slate-50 text-right text-slate-700">
                        <tr>
                          <th className="p-3"><input aria-label="انتخاب همه ردیف‌های این صفحه" type="checkbox" checked={allVisibleSelected} onChange={togglePageSelection} /></th>
                          <th className="p-3 font-semibold">ردیف</th>
                          <th className="p-3">{columnButton("شماره کوتاژ", "full_serial_number")}</th>
                          <th className="p-3">{columnButton("تاریخ", "cottage_date")}</th>
                          <th className="p-3">{columnButton("وضعیت", "declaration_status")}</th>
                          <th className="p-3">{columnButton("گمرک اظهار", "customs_name")}</th>
                          <th className="p-3">کشور مقصد</th>
                          <th className="p-3">کالا</th>
                          <th className="p-3">{columnButton("ارزش", "total_value")}</th>
                          <th className="p-3">ارزش ریالی</th>
                          <th className="p-3">نرخ ارز</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {pageItems.map((cottage, index) => (
                          <tr key={cottage.id} className="transition hover:bg-slate-50">
                            <td className="p-3"><input aria-label={`انتخاب ${cottage.full_serial_number}`} type="checkbox" checked={selectedIds.includes(cottage.id)} onChange={() => toggleSelected(cottage.id)} /></td>
                            <td className="p-3 text-slate-500">{((page - 1) * pageSize + index + 1).toLocaleString("fa-IR")}</td>
                            <td className="whitespace-nowrap p-3 font-semibold">
                              <button type="button" onClick={() => navigate(`/export-cottages/${encodeURIComponent(cottage.full_serial_number)}`)} className="text-blue-700 hover:underline">
                                {cottage.full_serial_number}
                              </button>
                            </td>
                            <td className="whitespace-nowrap p-3">{String(cottage.cottage_date || "—").replaceAll("-", "/")}</td>
                            <td className="whitespace-nowrap p-3"><span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700">{cottage.declaration_status || "—"}</span></td>
                            <td className="whitespace-nowrap p-3">{cottage.customs_name || "—"}</td>
                            <td className="whitespace-nowrap p-3">{cottage.destination_country || "—"}</td>
                            <td className="whitespace-nowrap p-3">{formatNumber(cottage.quantity)}</td>
                            <td className="whitespace-nowrap p-3">{formatNumber(cottage.total_value)} <span className="text-xs text-slate-500">{cottage.currency_type}</span></td>
                            <td className="whitespace-nowrap p-3">{formatNumber(cottage.customs_value_epl)}</td>
                            <td className="whitespace-nowrap p-3">{formatNumber(cottage.currency_price)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="mt-4">
                <PaginationControls
                  currentPage={page}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  pageSizeOptions={[10, 20, 50, 100]}
                  hasNext={page < totalPages}
                  hasPrevious={page > 1}
                  onPageChange={(event) => setPage(Number(event.target.value))}
                  onNextPage={() => setPage((previous) => Math.min(totalPages, previous + 1))}
                  onPreviousPage={() => setPage((previous) => Math.max(1, previous - 1))}
                  onPageSizeChange={(event) => setPageSize(Number(event.target.value))}
                />
              </div>
            </>
          )}
        </section>
      </main>

      {selectedIds.length > 0 && (
        <div className="fixed inset-x-0 bottom-4 z-20 px-4">
          <div className="mx-auto flex max-w-4xl flex-col gap-3 rounded-3xl bg-white p-4 shadow-xl ring-1 ring-slate-200 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm text-slate-700"><strong>{selectedIds.length.toLocaleString("fa-IR")}</strong> اظهارنامه انتخاب شده</span>
            <div className="flex gap-2">
              <button type="button" onClick={() => setSelectedIds([])} className="rounded-xl px-4 py-2 text-sm text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">لغو انتخاب</button>
              <button type="button" onClick={() => setConfirmDelete(true)} className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">
                <Trash2 className="h-4 w-4" /> حذف انتخاب‌شده‌ها
              </button>
            </div>
          </div>
        </div>
      )}

      <FilterSlideOver
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="فیلتر اظهارنامه‌های صادراتی"
        badgeCount={activeFilterCount}
        onClear={clearFilters}
        onApply={() => setFiltersOpen(false)}
        dir="rtl"
      >
        <AccordionItem title="وضعیت و مشخصات" defaultOpen>
          <div className="grid gap-4 sm:grid-cols-2">
            <FilterSelect label="وضعیت اظهارنامه" value={filters.status} options={distinctOptions.declaration_status || []} onChange={(value) => updateFilter("status", value)} />
            <FilterSelect label="نوع ارز" value={filters.currency} options={distinctOptions.currency_type || []} onChange={(value) => updateFilter("currency", value)} />
            <FilterSelect label="گمرک اظهار" value={filters.customs} options={distinctOptions.customs_name || []} onChange={(value) => updateFilter("customs", value)} />
            <FilterSelect label="کشور مقصد" value={filters.destination} options={distinctOptions.destination_country || []} onChange={(value) => updateFilter("destination", value)} />
            <FilterSelect label="روش حمل" value={filters.transport} options={distinctOptions.transport_method || []} onChange={(value) => updateFilter("transport", value)} />
          </div>
        </AccordionItem>

        <AccordionItem title="بازه‌های تاریخ">
          <div className="grid gap-4 sm:grid-cols-2">
            <DateFilter label="تاریخ اظهار از" value={filters.dateFrom} onChange={(value) => updateFilter("dateFrom", value)} />
            <DateFilter label="تاریخ اظهار تا" value={filters.dateTo} onChange={(value) => updateFilter("dateTo", value)} />
            <DateFilter label="تاریخ پرداخت از" value={filters.paymentFrom} onChange={(value) => updateFilter("paymentFrom", value)} />
            <DateFilter label="تاریخ پرداخت تا" value={filters.paymentTo} onChange={(value) => updateFilter("paymentTo", value)} />
          </div>
        </AccordionItem>

        <AccordionItem title="بازه ارزش اظهارنامه">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1.5 text-sm text-slate-700">
              <span className="font-medium">حداقل ارزش</span>
              <input className={inputClass} type="number" min="0" value={filters.valueMin} onChange={(event) => updateFilter("valueMin", event.target.value)} placeholder="از" />
            </label>
            <label className="space-y-1.5 text-sm text-slate-700">
              <span className="font-medium">حداکثر ارزش</span>
              <input className={inputClass} type="number" min="0" value={filters.valueMax} onChange={(event) => updateFilter("valueMax", event.target.value)} placeholder="تا" />
            </label>
          </div>
        </AccordionItem>
      </FilterSlideOver>
    </div>
  );
};

export default ExportCottageList;
